export type CodeTokenKind =
  | 'plain'
  | 'comment'
  | 'keyword'
  | 'string'
  | 'property'
  | 'number'
  | 'operator'
  | 'punctuation'
  | 'function'
  | 'variable';

export type CodeHighlightToken = {
  kind: CodeTokenKind;
  value: string;
};

const PYTHON_KEYWORDS = new Set([
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'False',
  'finally',
  'for',
  'from',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'None',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'True',
  'try',
  'while',
  'with',
  'yield',
]);

const PYTHON_BUILTINS = new Set(['dict', 'int', 'json', 'len', 'list', 'os', 'print', 'range', 'set', 'str']);

const JAVASCRIPT_KEYWORDS = new Set([
  'async',
  'await',
  'break',
  'case',
  'catch',
  'class',
  'const',
  'continue',
  'default',
  'else',
  'export',
  'false',
  'finally',
  'for',
  'from',
  'function',
  'if',
  'import',
  'in',
  'let',
  'new',
  'null',
  'return',
  'switch',
  'throw',
  'true',
  'try',
  'typeof',
  'undefined',
  'while',
]);

const JAVASCRIPT_BUILTINS = new Set([
  'Array',
  'Boolean',
  'Date',
  'Error',
  'JSON',
  'Math',
  'Number',
  'Object',
  'Promise',
  'String',
  'WebSocket',
  'console',
  'crypto',
  'fetch',
  'process',
]);

const BASH_KEYWORDS = new Set(['curl', 'export', 'for', 'if', 'then', 'else', 'fi', 'do', 'done']);

const isIdentifierStart = (char: string) => /[A-Za-z_$]/.test(char);
const isIdentifierPart = (char: string) => /[A-Za-z0-9_$-]/.test(char);
const isDigit = (char: string) => /\d/.test(char);
const isWhitespace = (char: string) => /\s/.test(char);

function pushToken(tokens: CodeHighlightToken[], kind: CodeTokenKind, value: string) {
  if (!value) return;
  const previous = tokens[tokens.length - 1];
  if (previous?.kind === kind) {
    previous.value += value;
    return;
  }
  tokens.push({ kind, value });
}

function readQuotedString(code: string, start: number): number {
  const quote = code[start];
  let index = start + 1;
  while (index < code.length) {
    if (code[index] === '\\') {
      index += 2;
      continue;
    }
    if (code[index] === quote) {
      return index + 1;
    }
    index += 1;
  }
  return code.length;
}

function readNumber(code: string, start: number): number {
  let index = start;
  while (index < code.length && /[0-9._-]/.test(code[index] ?? '')) {
    index += 1;
  }
  return index;
}

function readIdentifier(code: string, start: number): number {
  let index = start + 1;
  while (index < code.length && isIdentifierPart(code[index] ?? '')) {
    index += 1;
  }
  return index;
}

function highlightJson(code: string): CodeHighlightToken[] {
  const tokens: CodeHighlightToken[] = [];
  let index = 0;

  while (index < code.length) {
    const char = code[index] ?? '';
    const next = code[index + 1] ?? '';

    if (char === '/' && next === '/') {
      const end = code.indexOf('\n', index);
      const commentEnd = end === -1 ? code.length : end;
      pushToken(tokens, 'comment', code.slice(index, commentEnd));
      index = commentEnd;
      continue;
    }

    if (char === '"') {
      const end = readQuotedString(code, index);
      const after = code.slice(end).match(/^\s*:/);
      pushToken(tokens, after ? 'property' : 'string', code.slice(index, end));
      index = end;
      continue;
    }

    if (isDigit(char) || (char === '-' && isDigit(next))) {
      const end = readNumber(code, index);
      pushToken(tokens, 'number', code.slice(index, end));
      index = end;
      continue;
    }

    const literal = code.slice(index).match(/^(true|false|null)\b/);
    if (literal) {
      pushToken(tokens, 'keyword', literal[0]);
      index += literal[0].length;
      continue;
    }

    if (/[\{\}\[\]:,]/.test(char)) {
      pushToken(tokens, 'punctuation', char);
      index += 1;
      continue;
    }

    pushToken(tokens, 'plain', char);
    index += 1;
  }

  return tokens;
}

function highlightCodeLike(
  code: string,
  language: 'python' | 'javascript' | 'bash',
): CodeHighlightToken[] {
  const tokens: CodeHighlightToken[] = [];
  let index = 0;
  let atCommandPosition = true;

  const keywords = language === 'python' ? PYTHON_KEYWORDS : language === 'javascript' ? JAVASCRIPT_KEYWORDS : BASH_KEYWORDS;
  const builtins = language === 'python' ? PYTHON_BUILTINS : language === 'javascript' ? JAVASCRIPT_BUILTINS : new Set<string>();
  const lineComment = language === 'python' || language === 'bash' ? '#' : '//';

  while (index < code.length) {
    const char = code[index] ?? '';
    const next = code[index + 1] ?? '';

    if (char === '\n') {
      pushToken(tokens, 'plain', char);
      atCommandPosition = true;
      index += 1;
      continue;
    }

    if (isWhitespace(char)) {
      pushToken(tokens, 'plain', char);
      index += 1;
      continue;
    }

    if (code.startsWith(lineComment, index)) {
      const end = code.indexOf('\n', index);
      const commentEnd = end === -1 ? code.length : end;
      pushToken(tokens, 'comment', code.slice(index, commentEnd));
      index = commentEnd;
      continue;
    }

    if (language === 'javascript' && char === '/' && next === '*') {
      const end = code.indexOf('*/', index + 2);
      const commentEnd = end === -1 ? code.length : end + 2;
      pushToken(tokens, 'comment', code.slice(index, commentEnd));
      index = commentEnd;
      continue;
    }

    if (char === '"' || char === "'" || (language === 'javascript' && char === '`')) {
      const end = readQuotedString(code, index);
      pushToken(tokens, 'string', code.slice(index, end));
      index = end;
      atCommandPosition = false;
      continue;
    }

    if (language === 'bash' && char === '$') {
      const variable = code.slice(index).match(/^\$\{?[A-Za-z_][A-Za-z0-9_]*\}?/);
      if (variable) {
        pushToken(tokens, 'variable', variable[0]);
        index += variable[0].length;
        atCommandPosition = false;
        continue;
      }
    }

    if (language === 'bash' && char === '-' && next === '-') {
      const flag = code.slice(index).match(/^--?[A-Za-z0-9_-]+/);
      if (flag) {
        pushToken(tokens, 'operator', flag[0]);
        index += flag[0].length;
        atCommandPosition = false;
        continue;
      }
    }

    if (isDigit(char)) {
      const end = readNumber(code, index);
      pushToken(tokens, 'number', code.slice(index, end));
      index = end;
      atCommandPosition = false;
      continue;
    }

    if (isIdentifierStart(char)) {
      const end = readIdentifier(code, index);
      const word = code.slice(index, end);
      const nextNonSpace = code.slice(end).match(/^\s*(\()/);
      const kind: CodeTokenKind =
        language === 'bash' && atCommandPosition
          ? 'function'
          : keywords.has(word)
            ? 'keyword'
            : builtins.has(word)
              ? 'function'
              : /^[A-Z]/.test(word)
                ? 'function'
              : nextNonSpace
                ? 'function'
                : 'plain';
      pushToken(tokens, kind, word);
      index = end;
      atCommandPosition = false;
      continue;
    }

    if (/[()[\]{}.,:;=+*<>|&!?\\]/.test(char)) {
      pushToken(tokens, 'punctuation', char);
      index += 1;
      atCommandPosition = char === '\\';
      continue;
    }

    pushToken(tokens, 'plain', char);
    index += 1;
    atCommandPosition = false;
  }

  return tokens;
}

export function highlightCode(code: string, language: string): CodeHighlightToken[] {
  const normalized = language.toLowerCase();
  if (normalized === 'json') {
    return highlightJson(code);
  }
  if (normalized === 'python' || normalized === 'py') {
    return highlightCodeLike(code, 'python');
  }
  if (normalized === 'javascript' || normalized === 'js' || normalized === 'typescript' || normalized === 'ts') {
    return highlightCodeLike(code, 'javascript');
  }
  if (normalized === 'bash' || normalized === 'shell' || normalized === 'sh' || normalized === 'curl') {
    return highlightCodeLike(code, 'bash');
  }
  return [{ kind: 'plain', value: code }];
}
